interface EnvConfig {
  // API Configuration
  baseUrl: string;

  // Auth0 Configuration
  authDomain: string;
  authClientId: string;
  authAudience: string;

  // Application Configuration
  nodeEnv: "development" | "production" | "staging" | "testing";
  isProduction: boolean;
  isDevelopment: boolean;
  isStaging: boolean;
  isTesting: boolean;
}

class EnvironmentConfig {
  private config: EnvConfig;

  constructor() {
    this.config = this.loadAndValidateConfig();
  }

  private loadAndValidateConfig(): EnvConfig {
    const requiredVars = [
      "REACT_APP_BASE_URL",
      "REACT_APP_AUTH_DOMAIN",
      "REACT_APP_AUTH_WEB_CLIENT",
      "REACT_APP_AUTH_AUDIENCE",
    ];

    const missingVars = requiredVars.filter((varName) => !process.env[varName]);

    if (missingVars.length > 0) {
      throw new Error(
        `Missing required environment variables: ${missingVars.join(", ")}\n` +
          "Please check your .env file and ensure all required variables are set."
      );
    }

    const nodeEnv = (process.env.NODE_ENV ||
      "development") as EnvConfig["nodeEnv"];

    return {
      // API Configuration
      baseUrl: this.getRequiredEnvVar("REACT_APP_BASE_URL"),

      // Auth0 Configuration
      authDomain: this.getRequiredEnvVar("REACT_APP_AUTH_DOMAIN"),
      authClientId: this.getRequiredEnvVar("REACT_APP_AUTH_WEB_CLIENT"),
      authAudience: this.getRequiredEnvVar("REACT_APP_AUTH_AUDIENCE"),

      // Application Configuration
      nodeEnv,
      isProduction: nodeEnv === "production",
      isDevelopment: nodeEnv === "development",
      isStaging: nodeEnv === "staging",
      isTesting: nodeEnv === "testing",
    };
  }

  private getRequiredEnvVar(varName: string): string {
    const value = process.env[varName];
    if (!value) {
      throw new Error(`Required environment variable ${varName} is not set`);
    }
    return value;
  }

  // Public getters for configuration values
  get baseUrl(): string {
    return this.config.baseUrl;
  }

  get authDomain(): string {
    return this.config.authDomain;
  }

  get authClientId(): string {
    return this.config.authClientId;
  }

  get authAudience(): string {
    return this.config.authAudience;
  }

  get nodeEnv(): EnvConfig["nodeEnv"] {
    return this.config.nodeEnv;
  }

  get isProduction(): boolean {
    return this.config.isProduction;
  }

  get isDevelopment(): boolean {
    return this.config.isDevelopment;
  }

  get isStaging(): boolean {
    return this.config.isStaging;
  }

  get isTesting(): boolean {
    return this.config.isTesting;
  }

  // Utility method to check if logging should be enabled
  get shouldLog(): boolean {
    return this.isDevelopment || this.isStaging;
  }

  // Method to get all config
  getConfig(): Readonly<EnvConfig> {
    if (!this.isDevelopment) {
      throw new Error("Config access is only allowed in development mode");
    }
    return Object.freeze({ ...this.config });
  }
}

export const envConfig = new EnvironmentConfig();
export default envConfig;
